/* Copyright (c) 2021-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Customer } from "~/components/BasicCustomerInfo/types";
import { InlineEditDatePicker } from "~/components/InlineEdit/InlineEditDatePicker";
import { InlineEditDropdown } from "~/components/InlineEdit/InlineEditDropdown";
import { InlineEditMultiSelect } from "~/components/InlineEdit/InlineEditMultiSelect";
import { InlineEditSwitch } from "~/components/InlineEdit/InlineEditSwitch";
import { InlineEditText } from "~/components/InlineEdit/InlineEditText";
import { DetailRow } from "~/components/Utils/DetailRow";
import { Loading } from "~/components/Utils/Loading";
import { countryList } from "~/constants";
import { customerCategoryList } from "~/constants/customerCategoryList";
import { languageList } from "~/constants/languageList";
import { useAPIData } from "~/hooks/useAPIData";

const cdtOptions = ["DACH 1", "DACH 2", "Nordic 1", "Nordic 2", "UK", "India"];

const segmentOptions = [
  { value: "small_business", label: "Small business" },
  { value: "corporate", label: "Corporate" },
  { value: "emerald", label: "Emerald" },
];

type CustomerDetailsProps = {
  item: Customer;
  onSubmit: (values: Customer) => Promise<void>;
  canEdit?: boolean;
};

function CustomerDetails({ item, onSubmit, canEdit }: CustomerDetailsProps) {
  const [timeZones, timeZonesLoading] = useAPIData<string[]>(
    "/ui-data",
    (data) => data.timezones,
    []
  );

  return (
    <>
      <DetailRow title="Country">
        <InlineEditDropdown
          name="country"
          options={countryList}
          defaultValue={item.location.country}
          onSubmit={(country) =>
            onSubmit({ ...item, location: { ...item.location, country } })
          }
          canEdit={canEdit}
        />
      </DetailRow>

      <DetailRow title="Address" tooltip="Customer office address">
        <InlineEditText
          name="address"
          defaultValue={item.location.address}
          onSubmit={(address) =>
            onSubmit({ ...item, location: { ...item.location, address } })
          }
          canEdit={canEdit}
        />
      </DetailRow>

      <DetailRow title="Time zone">
        {timeZonesLoading ? (
          <Loading />
        ) : (
          <InlineEditDropdown
            name="location"
            options={timeZones}
            defaultValue={item.location.location}
            onSubmit={(location) =>
              onSubmit({ ...item, location: { ...item.location, location } })
            }
            canEdit={canEdit}
          />
        )}
      </DetailRow>

      <DetailRow
        title="Languages"
        tooltip="Languages used for communicating with the Customer representatives"
      >
        <InlineEditMultiSelect
          options={languageList}
          defaultValue={item.location.languages || []}
          onSubmit={(languages) =>
            onSubmit({ ...item, location: { ...item.location, languages } })
          }
          canEdit={canEdit}
        />
      </DetailRow>

      <DetailRow title="Customer category">
        <InlineEditMultiSelect
          options={customerCategoryList}
          defaultValue={item.info.categories || []}
          onSubmit={(categories) =>
            onSubmit({ ...item, info: { ...item.info, categories } })
          }
          canEdit={!!item.klarityId && canEdit}
        />
      </DetailRow>

      <DetailRow title="Onboarding Date">
        <InlineEditDatePicker
          defaultValue={item.info.onboardingDate}
          onSubmit={(onboardingDate) =>
            onSubmit({ ...item, info: { ...item.info, onboardingDate } })
          }
          canEdit={canEdit}
          clearable
        />
      </DetailRow>

      <DetailRow title="Offboarding Date">
        <InlineEditDatePicker
          defaultValue={item.info.offboardingDate}
          onSubmit={(offboardingDate) =>
            onSubmit({ ...item, info: { ...item.info, offboardingDate } })
          }
          canEdit={canEdit}
          clearable
        />
      </DetailRow>

      <DetailRow title="Premium Support CSPs">
        <InlineEditMultiSelect
          defaultValue={item.info.premiumSupportCSPs || []}
          onSubmit={(premiumSupportCSPs) =>
            onSubmit({ ...item, info: { ...item.info, premiumSupportCSPs } })
          }
          canEdit={canEdit}
          options={["AWS", "Azure", "GCP"]}
        />
      </DetailRow>

      <DetailRow title="IBM Customer">
        <InlineEditSwitch
          trueLabel="Yes"
          falseLabel="No"
          defaultValue={item.info.ibmCustomer}
          onSubmit={(ibmCustomer) =>
            onSubmit({ ...item, info: { ...item.info, ibmCustomer } })
          }
          canEdit={canEdit}
        />
      </DetailRow>

      <DetailRow title="Customer Segment">
        <InlineEditDropdown
          name="segment"
          options={segmentOptions}
          defaultValue={item.info.segment}
          onSubmit={(segment) =>
            onSubmit({ ...item, info: { ...item.info, segment } })
          }
          canEdit={canEdit}
        />
      </DetailRow>

      <DetailRow title="Region (CDT)">
        <InlineEditMultiSelect
          options={cdtOptions}
          defaultValue={item.info.cdt || []}
          onSubmit={(cdt) => onSubmit({ ...item, info: { ...item.info, cdt } })}
          canEdit={canEdit}
        />
      </DetailRow>

      <DetailRow title="MC Teams">
        <InlineEditMultiSelect
          options={["MCE", "Capacity"]}
          defaultValue={item.info.mcTeams || []}
          onSubmit={(mcTeams) =>
            onSubmit({ ...item, info: { ...item.info, mcTeams } })
          }
          canEdit={canEdit}
        />
      </DetailRow>
    </>
  );
}

export { CustomerDetails };
